import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import { isNone } from '@ember/utils';
import type { htmlSafe } from '@ember/template';
import AditoolsRenderer from '../modifiers/aditools-renderer.ts';
type SafeString = ReturnType<typeof htmlSafe>;

export interface AditoolsDialogSignature {
  Element: HTMLDialogElement;
  Args: {
    title: string | SafeString;
    close: () => void;
    dialogClass?: string;
    suppressFocusTrap?: boolean;
    hideCloseX?: boolean;
    hideTitlebar?: boolean;
  };
  Blocks: {
    default: [closeDialog: () => void];
    footer: [closeDialog: () => void];
  };
}
type AditoolsDialogArgs = AditoolsDialogSignature['Args'];
export default class AditoolsDialog extends Component<AditoolsDialogSignature> {
  renderer = AditoolsRenderer;
  @tracked lastFocus?: HTMLElement | null;
  dialogElement?: HTMLDialogElement;

  constructor(owner: unknown, args: AditoolsDialogArgs) {
    super(owner, args);
    assert('`title` is required', !isNone(this.args.title));
    assert('`close` is required', !isNone(this.args.close));
  }

  get _suppressFocusTrap() {
    // Return suppressFocusTrap value if explicitly supplied.
    // Otherwise, return true if hideCloseX explicitly supplied as true or false otherwise.
    return this.args.suppressFocusTrap ?? this.args.hideCloseX ?? false;
  }

  get _enableFocusTrap() {
    return !this._suppressFocusTrap;
  }

  get _hideCloseX() {
    return this.args.hideCloseX ?? false;
  }

  get _hideTitlebar() {
    return this.args.hideTitlebar ?? false;
  }

  @action closeDialog() {
    if (this.dialogElement) {
      this.dialogElement.close();
    }
  }

  @action guardCancel(event: Event) {
    if (this._hideCloseX) {
      event.preventDefault();
    }
  }

  @action dialogClosed() {
    this.args.close();
  }

  @action inserted(element: HTMLElement) {
    //N.B. document may be null or not have activeElement if we're in FastBoot
    this.lastFocus = document.activeElement as HTMLElement;
    this.dialogElement = element.querySelector('dialog') as HTMLDialogElement;
    this.dialogElement.showModal();
    (document.querySelector('body') as HTMLElement).style.overflow = 'hidden';
  }

  @action destroying() {
    //N.B. document may be null or not have activeElement if we're in FastBoot
    if (this.lastFocus && this.lastFocus.focus) {
      //SVG focusable elements don't have a focus() method in IE & Edge
      this.lastFocus.focus();
    }
    (document.querySelector('body') as HTMLElement).style.overflow = 'auto';
    this.dialogElement = undefined;
  }
}
